import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";
import { getMovieById } from "@/mocks/movies";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const count = heroSlides.length;

  const go = useCallback(
    (next: number) => setIndex((next + count) % count),
    [count],
  );

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [count]);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      go(diff > 0 ? index - 1 : index + 1);
    }
    touchStartX.current = null;
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {heroSlides.map((slide, i) => {
        const movie = getMovieById(slide.movieId);
        const active = i === index;
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              active ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={movie?.backdrop}
              alt={`${slide.title} 焦点剧照`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/20" />

            <div className="relative h-full w-full flex items-center">
              <div className="w-full px-4 md:px-8 lg:px-12">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-label font-bold mb-4 md:mb-5">
                    <i className="ri-fire-fill" />
                    {slide.label}
                  </span>
                  <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-3 md:mb-5">
                    {slide.title}
                  </h2>
                  <p className="text-background-200/90 text-sm md:text-base leading-relaxed mb-6 md:mb-8 line-clamp-3 max-w-xl">
                    {slide.summary}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slide.movieId}`}
                      className="inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      观看详情
                    </Link>
                    <span className="inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md border border-background-50/30 text-background-50 font-medium text-sm">
                      <i className="ri-star-fill text-accent-400" />
                      {movie?.rating.toFixed(1)} 分
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* controls */}
      <div className="absolute bottom-5 md:bottom-8 left-0 w-full z-20 px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {heroSlides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到 ${slide.title}`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index
                    ? "w-8 bg-accent-500"
                    : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="上一张"
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="下一张"
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}